/**
 * Sicklo - Cookie Consent Banner
 * UK GDPR & ICO Compliant Cookie Consent
 * Features: Accept All, Essential Only, Custom Preferences
 */

import React, { useState, useEffect } from 'react';
import { X, Cookie, ChevronDown, ChevronUp, Shield, Settings } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'sicklo_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'sicklo_cookie_preferences';
const COOKIE_SHOW_BANNER_KEY = 'sicklo_show_cookie_banner';

const defaultPreferences = {
  essential: true,
  functional: false,
  analytics: false,
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const forceShowBanner = localStorage.getItem(COOKIE_SHOW_BANNER_KEY);
    
    // Check if user explicitly requested to show banner (from Update Cookie Preferences button)
    if (forceShowBanner === 'true') {
      localStorage.removeItem(COOKIE_SHOW_BANNER_KEY);
      // Load current preferences for editing
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch (e) {
          setPreferences(defaultPreferences);
        }
      }
      setShowDetails(true); // Open with details expanded
      setShowBanner(true);
      return;
    }
    
    if (!consent) {
      // Only show cookie consent on login/register pages, not on landing page
      const path = window.location.pathname;
      const isAuthPage = path.includes('/login') || 
                         path.includes('/register') || 
                         path.includes('/signup') ||
                         path.includes('/sign-up');
      
      if (isAuthPage) {
        const timer = setTimeout(() => setShowBanner(true), 500);
        return () => clearTimeout(timer);
      }
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch (e) {
          setPreferences(defaultPreferences);
        }
      }
    }
    
    // Listen for show cookie banner event
    const handleShowBanner = () => {
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch (e) {
          setPreferences(defaultPreferences);
        }
      }
      setShowDetails(true);
      setShowBanner(true);
    };
    
    window.addEventListener('showCookieBanner', handleShowBanner);
    return () => window.removeEventListener('showCookieBanner', handleShowBanner);
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true,
    });
  };

  const handleAcceptNecessary = () => {
    saveConsent(defaultPreferences);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[9998]" data-testid="cookie-consent-banner">
      <div className="bg-white rounded-xl shadow-2xl border border-neutral-200 overflow-hidden">
        {/* Banner Header */}
        <div className="p-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-[#FF3333]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Cookie className="text-[#FF3333]" size={16} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-neutral-900 text-xs">Cookie Preferences</h3>
                <button 
                  onClick={handleAcceptNecessary}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors p-0.5 -mr-1"
                  aria-label="Close and accept essential only"
                  data-testid="cookie-close-btn"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-[11px] text-neutral-600 mt-0.5 leading-relaxed">
                We use cookies to keep you logged in.{' '}
                <a href="/privacy#cookies" className="text-[#FF3333] hover:underline">
                  Learn more
                </a>
              </p>
            </div>
          </div>

          {/* Toggle Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-[11px] font-medium text-[#FF3333] hover:text-[#cc2929] mt-2 transition-colors"
            data-testid="cookie-details-toggle"
          >
            <Settings size={12} />
            Manage Preferences
            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {/* Cookie Categories - Expandable */}
          {showDetails && (
            <div className="mt-2 space-y-1.5 p-2 bg-neutral-50 rounded-lg text-[11px]" data-testid="cookie-preferences-panel">
              {/* Essential */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-neutral-900">Essential</span>
                  <span className="ml-1 text-[9px] bg-neutral-200 text-neutral-600 px-1 py-0.5 rounded-full">Required</span>
                </div>
                <div className="w-8 h-4 bg-[#FF3333] rounded-full relative opacity-60 cursor-not-allowed">
                  <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow"></div>
                </div>
              </div>
              
              {/* Functional */}
              <div className="flex items-center justify-between">
                <span className="font-medium text-neutral-900">Functional</span>
                <button 
                  onClick={() => togglePreference('functional')}
                  className={`w-8 h-4 rounded-full relative transition-colors ${preferences.functional ? 'bg-[#FF3333]' : 'bg-neutral-300'}`}
                  data-testid="cookie-functional-toggle"
                >
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${preferences.functional ? 'right-0.5' : 'left-0.5'}`}></div>
                </button>
              </div>
              
              {/* Analytics */}
              <div className="flex items-center justify-between">
                <span className="font-medium text-neutral-900">Analytics</span>
                <button 
                  onClick={() => togglePreference('analytics')}
                  className={`w-8 h-4 rounded-full relative transition-colors ${preferences.analytics ? 'bg-[#FF3333]' : 'bg-neutral-300'}`}
                  data-testid="cookie-analytics-toggle"
                >
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${preferences.analytics ? 'right-0.5' : 'left-0.5'}`}></div>
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-3 py-1.5 bg-[#FF3333] text-white font-semibold rounded-lg hover:bg-[#E62E2E] transition-colors text-[11px]"
              data-testid="cookie-accept-all"
            >
              Accept All
            </button>
            {showDetails ? (
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-3 py-1.5 bg-neutral-100 text-neutral-700 font-semibold rounded-lg hover:bg-neutral-200 transition-colors text-[11px]"
                data-testid="cookie-save-preferences"
              >
                Save Choices
              </button>
            ) : (
              <button
                onClick={handleAcceptNecessary}
                className="flex-1 px-3 py-1.5 bg-neutral-100 text-neutral-700 font-semibold rounded-lg hover:bg-neutral-200 transition-colors text-[11px]"
                data-testid="cookie-accept-necessary"
              >
                Essential Only
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-1.5 bg-neutral-50 border-t border-neutral-100 flex items-center justify-between">
          <div className="flex items-center gap-1 text-[9px] text-neutral-500">
            <Shield size={10} />
            <span>UK GDPR Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Utility functions for other components to check cookie preferences
export const getCookiePreferences = () => {
  const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (savedPrefs) {
    try {
      return JSON.parse(savedPrefs);
    } catch (e) {
      return defaultPreferences;
    }
  }
  return defaultPreferences;
};

export const isCookieAllowed = (category) => {
  const prefs = getCookiePreferences();
  return prefs[category] === true;
};

export const saveCookiePreferences = (preferences) => {
  // Save consent and preferences
  localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
  localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
  return true;
};

export const resetCookieConsent = () => {
  // Dispatch event to show cookie banner without page reload
  window.dispatchEvent(new CustomEvent('showCookieBanner'));
};

export default CookieConsent;
